import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import NumberInput from "@/components/base/NumberInput";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { InventoryItem, Product, Promotion } from "@/types";
import { formatCOP } from "@/utils/format";

interface PromotionFormModalProps {
  open: boolean;
  products: Product[];
  inventory: InventoryItem[];
  onClose: () => void;
  onSubmit: (p: Promotion) => void;
}

interface ComboItem {
  productId: string;
  qty: number;
}

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

const totalStock = (inv: InventoryItem | undefined): number =>
  !inv
    ? 0
    : (inv.stockCasa ?? 0) +
      (inv.stockPuntoClear ?? 0) +
      Object.values(inv.stockByAdvisor ?? {}).reduce((s, n) => s + n, 0) +
      Object.values(inv.stockOwnedByAdvisor ?? {}).reduce((s, n) => s + n, 0);

const PromotionFormModal = ({
  open,
  products = [],
  inventory = [],
  onClose,
  onSubmit,
}: PromotionFormModalProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [discountPct, setDiscountPct] = useState(15);
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(inDays(15));
  const [items, setItems] = useState<ComboItem[]>([]);
  const [pickId, setPickId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setTitle("");
    setDescription("");
    setDiscountPct(15);
    setStartDate(today());
    setEndDate(inDays(15));
    setItems([]);
    setPickId("");
  }, [open]);

  const stockProducts = useMemo(
    () => products.filter((p) => totalStock(inventory.find((i) => i.productId === p.id)) > 0),
    [products, inventory]
  );
  const options = stockProducts.length > 0 ? stockProducts : products;

  const priceOf = (productId: string): number =>
    products.find((p) => p.id === productId)?.pricePublic ?? 0;
  const nameOf = (productId: string): string =>
    products.find((p) => p.id === productId)?.name ?? "Producto";

  const comboSum = items.reduce((a, it) => a + priceOf(it.productId) * it.qty, 0);
  const comboPrice = Math.round(comboSum * (1 - (Number(discountPct) || 0) / 100));
  const savings = comboSum - comboPrice;

  const addItem = () => {
    if (!pickId) return;
    setItems((prev) => {
      const existing = prev.find((i) => i.productId === pickId);
      if (existing) return prev.map((i) => (i.productId === pickId ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, { productId: pickId, qty: 1 }];
    });
    setPickId("");
  };

  const setQty = (productId: string, qty: number) =>
    setItems((prev) => prev.map((i) => (i.productId === productId ? { ...i, qty } : i)));
  const removeItem = (productId: string) =>
    setItems((prev) => prev.filter((i) => i.productId !== productId));

  const submit = () => {
    if (!title.trim()) {
      setError("Escribe un título para la promoción.");
      return;
    }
    if (Number(discountPct) <= 0) {
      setError("El descuento debe ser mayor a cero.");
      return;
    }
    const promo: Promotion = {
      id: "",
      title: title.trim(),
      description: description.trim(),
      discountPct: Number(discountPct) || 0,
      startDate,
      endDate,
      createdAt: new Date().toISOString(),
      items,
    };
    onSubmit(promo);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nueva promoción o combo"
      icon="ri-megaphone-line"
      size="lg"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} className={primaryButtonClass}>
            <i className="ri-megaphone-line" /> Publicar y difundir
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={labelClass}>Título</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ej: Combo Hogar Impecable -20%"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Descripción</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Describe los productos incluidos y las condiciones de la oferta."
            className={`${inputClass} resize-none`}
          />
          <p className="text-[10px] text-foreground-500 mt-1">{description.length}/500</p>
        </div>

        {/* Productos del combo (solo lo que hay en stock). */}
        <div className="rounded-lg border border-background-300/40 bg-background-200/30 p-3.5">
          <label className={labelClass}>Productos del combo (de tu stock)</label>
          <div className="flex flex-col sm:flex-row sm:items-end gap-2">
            <select value={pickId} onChange={(e) => setPickId(e.target.value)} className={inputClass}>
              <option value="" className="bg-background-100">
                — elige un producto —
              </option>
              {options.map((p) => (
                <option key={p.id} value={p.id} className="bg-background-100">
                  {p.name} · {formatCOP(p.pricePublic)}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={addItem}
              disabled={!pickId}
              className="px-3.5 py-2 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-40 text-foreground-950 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-1.5"
            >
              <i className="ri-add-line" /> Agregar
            </button>
          </div>

          {items.length === 0 ? (
            <p className="text-[11px] text-foreground-500 mt-3">
              Aún no agregaste productos. El combo puede ser de uno o varios.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {items.map((it) => (
                <li
                  key={it.productId}
                  className="flex items-center gap-2 rounded-md bg-background-100/60 border border-background-300/40 px-3 py-2"
                >
                  <span className="flex-1 min-w-0 text-[12px] text-foreground-800 truncate">
                    {nameOf(it.productId)}
                  </span>
                  <div className="w-20 shrink-0">
                    <NumberInput
                      min={1}
                      value={it.qty}
                      onChange={(n) => setQty(it.productId, n)}
                      className={inputClass}
                    />
                  </div>
                  <span className="text-[12px] font-semibold text-foreground-950 whitespace-nowrap w-24 text-right">
                    {formatCOP(priceOf(it.productId) * it.qty)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeItem(it.productId)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 cursor-pointer"
                    aria-label="Quitar del combo"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Descuento (%)</label>
            <NumberInput min={1} max={90} value={discountPct} onChange={setDiscountPct} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Desde</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Hasta</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputClass} />
          </div>
        </div>

        {/* Resumen del combo: suma catálogo − descuento. */}
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
            <p className="text-[10px] text-foreground-500">Suma catálogo</p>
            <p className="text-sm font-bold text-foreground-950">{formatCOP(comboSum)}</p>
          </div>
          <div className="rounded-md bg-accent-500/10 border border-accent-500/30 px-3 py-2">
            <p className="text-[10px] text-accent-300">Precio del combo (-{discountPct}%)</p>
            <p className="text-sm font-bold text-accent-200">{formatCOP(comboPrice)}</p>
          </div>
          <div className="rounded-md bg-secondary-500/10 border border-secondary-500/30 px-3 py-2">
            <p className="text-[10px] text-secondary-200">Ahorro</p>
            <p className="text-sm font-bold text-secondary-100">{formatCOP(savings)}</p>
          </div>
        </div>

        {error && (
          <p className="text-[11px] text-primary-200 flex items-center gap-1.5">
            <i className="ri-error-warning-line" /> {error}
          </p>
        )}

        <div className="rounded-md bg-accent-500/10 border border-accent-500/25 px-3 py-2.5 flex items-start gap-2">
          <i className="ri-notification-3-line text-accent-300 mt-0.5" />
          <p className="text-[11px] text-foreground-700">
            Al publicar, la promoción se difunde automáticamente como notificación a todos los
            asesores de tu red.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default PromotionFormModal;